import React, { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  Loader2,
  Mail,
  Link2,
  Share2,
  Users,
  CheckCircle2,
  Clock,
  RefreshCw,
  Inbox,
  Eye,
  AlertTriangle,
  Copy,
  Check,
  ShieldCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import {
  isInviteLifecycleSchemaError,
} from '@/governor/membership/MembershipInviteClaimRuntime';

/**
 * SentInvitesPanel — BULLETPROOF EDITION
 * ---------------------------------------------------------------
 * Owner-visibility surface for the full invite lifecycle:
 *
 *   sent     → row created, link out the door
 *   opened   → recipient hit the /invite landing page
 *   accepted → recipient signed up + bound to the family graph
 *   failed   → binding pipeline raised an error (recipient saw an
 *              error toast); the owner can manually resolve here
 *
 * Manual-approval fallback:
 *   For rows in the `failed` state, the owner can:
 *     - "Mark as joined"  → manually flag the invite accepted (when
 *       the relative confirmed elsewhere they already got in)
 *     - "Copy invite link" → re-share the same secure token link
 *     - The owner can also just send a fresh invite from the Link tab.
 *
 * This component only READS the invite rows the existing pipeline
 * writes, plus minor status updates on manual-resolve clicks.
 * It NEVER touches relationship logic, parent_ids, the family
 * graph, or membership/payment.
 */

interface InviteRow {
  id: number;
  recipient_email: string | null;
  recipient_phone: string | null;
  channel: string | null;
  status: string | null;
  token: string | null;
  created_at: string;
  opened_at: string | null;
  accepted_at: string | null;
  accepted_user_id: string | null;
  failed_at: string | null;
  failure_reason: string | null;
  expires_at: string | null;
  inviter_user_id: string | null;
  invite_type?: 'FAMILY_PROFILE' | 'SPONSORED_SEAT';
  delivery_channel?: string | null;
  seat_reservation_expires_at?: string | null;
  seat_claimed_at?: string | null;
  seat_returned_at?: string | null;
  membership_owner_user_id?: string | null;
  target_member_id?: string | null;
  cancelled_at?: string | null;
}

type InviteState =
  | 'accepted'
  | 'failed'
  | 'opened'
  | 'reserved'
  | 'returned'
  | 'cancelled'
  | 'expired'
  | 'sent';

const computeState = (
  inv: InviteRow,
): InviteState => {
  if (
    inv.accepted_user_id ||
    inv.seat_claimed_at ||
    inv.status === 'accepted'
  ) {
    return 'accepted';
  }

  if (
    inv.seat_returned_at ||
    inv.status === 'returned'
  ) {
    return 'returned';
  }

  if (inv.status === 'cancelled') {
    return 'cancelled';
  }

  if (
    inv.status === 'failed' ||
    inv.failed_at
  ) {
    return 'failed';
  }

  if (
    inv.invite_type === 'SPONSORED_SEAT' &&
    inv.seat_reservation_expires_at &&
    new Date(
      inv.seat_reservation_expires_at,
    ).getTime() < Date.now()
  ) {
    return 'expired';
  }

  if (
    inv.expires_at &&
    new Date(inv.expires_at).getTime() < Date.now()
  ) {
    return 'expired';
  }

  if (
    inv.invite_type === 'SPONSORED_SEAT' &&
    ['sent', 'opened', 'reserved'].includes(
      inv.status ?? '',
    )
  ) {
    return 'reserved';
  }

  if (
    inv.opened_at ||
    inv.status === 'opened'
  ) {
    return 'opened';
  }

  return 'sent';
};

const channelIcon = (channel: string | null) => {
  switch (channel) {
    case 'email':
      return <Mail className="w-3.5 h-3.5" />;
    case 'link':
      return <Link2 className="w-3.5 h-3.5" />;
    case 'contacts':
      return <Users className="w-3.5 h-3.5" />;
    case 'sms':
      return <Share2 className="w-3.5 h-3.5" />;
    case 'share':
    default:
      return <Share2 className="w-3.5 h-3.5" />;
  }
};

const channelLabel = (channel: string | null) => {
  switch (channel) {
    case 'email': return 'Email';
    case 'link': return 'Copied link';
    case 'contacts': return 'Contacts';
    case 'sms': return 'SMS';
    case 'share': return 'Shared';
    default: return channel || 'Invite';
  }
};

const formatRelative = (iso: string) => {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w ago`;
  return new Date(iso).toLocaleDateString();
};

const StatusPill: React.FC<{ state: InviteState }> = ({ state }) => {
  switch (state) {
    case 'accepted':
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
          <CheckCircle2 className="w-3 h-3" /> Joined
        </span>
      );
    case 'failed':
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-700 bg-rose-50 border border-rose-200 rounded-full px-2 py-0.5">
          <AlertTriangle className="w-3 h-3" /> Failed
        </span>
      );
    case 'expired':
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
          <Clock className="w-3 h-3" /> Expired
        </span>
      );
    case 'opened':
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
          <Eye className="w-3 h-3" /> Opened
        </span>
      );
    case 'reserved':
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5">
          <Clock className="w-3 h-3" /> Seat reserved
        </span>
      );
    case 'returned':
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
          <RefreshCw className="w-3 h-3" /> Seat returned
        </span>
      );
    case 'cancelled':
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
          <Clock className="w-3 h-3" /> Cancelled
        </span>
      );
    default:
      return (
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
          <Clock className="w-3 h-3" /> Sent
        </span>
      );
  }
};

const SentInvitesPanel: React.FC<{ refreshKey?: number }> = ({ refreshKey = 0 }) => {
  const { user, memberEmail } = useAuth();
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const applyOwnerFilter = (query: any) => {
        if (user?.id && memberEmail) {
          return query.or(
            `inviter_user_id.eq.${user.id},inviter_email.eq.${memberEmail}`,
          );
        }

        if (user?.id) {
          return query.eq(
            'inviter_user_id',
            user.id,
          );
        }

        if (memberEmail) {
          return query.eq(
            'inviter_email',
            memberEmail,
          );
        }

        return null;
      };

      const extendedFields =
        'id, recipient_email, recipient_phone, channel, status, token, created_at, opened_at, accepted_at, accepted_user_id, failed_at, failure_reason, expires_at, inviter_user_id, invite_type, delivery_channel, seat_reservation_expires_at, seat_claimed_at, seat_returned_at, membership_owner_user_id, target_member_id, cancelled_at';

      const legacyFields =
        'id, recipient_email, recipient_phone, channel, status, token, created_at, opened_at, accepted_at, accepted_user_id, failed_at, failure_reason, expires_at, inviter_user_id';

      let query = applyOwnerFilter(
        supabase
          .from('invites')
          .select(extendedFields)
          .order(
            'created_at',
            { ascending: false },
          )
          .limit(50),
      );

      if (!query) {
        setInvites([]);
        setLoading(false);
        return;
      }

      let {
        data,
        error: queryError,
      } = await query;

      if (
        queryError &&
        isInviteLifecycleSchemaError(
          queryError.message || '',
        )
      ) {
        query = applyOwnerFilter(
          supabase
            .from('invites')
            .select(legacyFields)
            .order(
              'created_at',
              { ascending: false },
            )
            .limit(50),
        );

        const legacyResult = await query;
        data = legacyResult.data;
        queryError = legacyResult.error;
      }

      if (queryError) {
        throw queryError;
      }

      const rows = ((data as InviteRow[]) || []).map(
        (row) => ({
          ...row,
          invite_type:
            row.invite_type ??
            'FAMILY_PROFILE',
        }),
      );

      const expiredSponsoredRows = rows.filter(
        (row) =>
          row.invite_type === 'SPONSORED_SEAT' &&
          !row.target_member_id &&
          !row.accepted_user_id &&
          !row.seat_returned_at &&
          Boolean(
            row.seat_reservation_expires_at,
          ) &&
          new Date(
            row.seat_reservation_expires_at!,
          ).getTime() <= Date.now(),
      );

      if (expiredSponsoredRows.length > 0) {
        const returnedAt =
          new Date().toISOString();

        await Promise.all(
          expiredSponsoredRows.map(
            (row) =>
              supabase
                .from('invites')
                .update({
                  status: 'expired',
                  seat_returned_at: returnedAt,
                })
                .eq('id', row.id)
                .is('accepted_user_id', null)
                .is('seat_returned_at', null),
          ),
        );

        for (const row of rows) {
          if (
            expiredSponsoredRows.some(
              (expired) =>
                expired.id === row.id,
            )
          ) {
            row.status = 'expired';
            row.seat_returned_at = returnedAt;
          }
        }
      }

      setInvites(rows);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : 'Could not load invites',
      );
      setInvites([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, memberEmail]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  // Build the invite URL for a row so the owner can copy/resend it
  // without going back to the Link tab (handy for failed rows).
  const buildInviteLink = useCallback(
    (row: InviteRow): string | null => {
      if (typeof window === 'undefined') return null;
      const base = window.location.origin;
      const inviterId = row.inviter_user_id || user?.id;
      if (!inviterId) return null;
      const t = row.token ? `?t=${row.token}` : '';
      return `${base}/invite/${inviterId}${t}`;
    },
    [user?.id],
  );

  const copyLink = async (row: InviteRow) => {
    const url = buildInviteLink(row);
    if (!url) {
      toast.error('Could not build invite link');
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(row.id);
      toast.success('Invite link copied!', {
        description: 'Paste it into any message app — email is not required.',
      });
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Could not copy link');
    }
  };

  // Manual-approve fallback: lets the tree owner mark a failed invite
  // as resolved (e.g. they confirmed elsewhere the relative already
  // got in, or they want to clear the row). This DOES NOT alter the
  // family graph — it only updates the invites row.
  const markAsHandled = async (row: InviteRow) => {
    if (row.target_member_id) {
      toast.error(
        'Person-targeted invitations can only be accepted by the verified recipient.',
      );
      return;
    }

    if (row.invite_type === 'SPONSORED_SEAT') {
      toast.error(
        'Sponsored seats must be claimed by the invited member.',
      );
      return;
    }

    try {
      await supabase
        .from('invites')
        .update({
          status: 'accepted',
          accepted_at: new Date().toISOString(),
          // accepted_user_id intentionally left null — the owner is
          // marking this as resolved without claiming a specific user.
          failure_reason: null,
        })
        .eq('id', row.id);
      toast.success('Marked as handled', {
        description: 'This invite will show as joined.',
      });
      load();
    } catch (e: any) {
      toast.error(e?.message || 'Could not update invite');
    }
  };

  const cancelTargetedInvite =
    async (row: InviteRow) => {
      if (!row.target_member_id) {
        return;
      }

      try {
        const { error } =
          await supabase.rpc(
            'cancel_person_targeted_invite',
            {
              p_invite_id: row.id,
            },
          );

        if (error) {
          throw error;
        }

        toast.success(
          'Person-specific invitation cancelled.',
          {
            description:
              'The invitation record and exact target certificate were preserved for audit history.',
          },
        );

        load();
      } catch (error: any) {
        toast.error(
          error?.message ||
            'Could not cancel this invitation.',
        );
      }
    };

  const total = invites.length;
  const states = invites.map(computeState);
  const accepted =
    states.filter(
      (state) => state === 'accepted',
    ).length;

  const opened =
    states.filter(
      (state) => state === 'opened',
    ).length;

  const reserved =
    states.filter(
      (state) => state === 'reserved',
    ).length;

  const returned =
    states.filter(
      (state) =>
        state === 'returned' ||
        state === 'cancelled' ||
        state === 'expired',
    ).length;

  const failed =
    states.filter(
      (state) => state === 'failed',
    ).length;

  const sent =
    states.filter(
      (state) => state === 'sent',
    ).length;

  return (
    <div className="space-y-3 pt-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-amber-900/80">
          Track every invite you've sent — sent, opened, joined, or failed.
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          className="text-amber-800 hover:bg-amber-100 h-8 px-2"
          aria-label="Refresh invite list"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Invite and sponsored-seat lifecycle */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        <div className="bg-white rounded-lg border border-amber-200 p-2 text-center">
          <div className="text-base font-serif font-bold text-amber-700">{sent}</div>
          <div className="text-[10px] uppercase tracking-wider text-amber-700/80">Sent</div>
        </div>
        <div className="bg-white rounded-lg border border-blue-200 p-2 text-center">
          <div className="text-base font-serif font-bold text-blue-700">{opened}</div>
          <div className="text-[10px] uppercase tracking-wider text-blue-700/80">Opened</div>
        </div>
        <div className="bg-white rounded-lg border border-emerald-200 p-2 text-center">
          <div className="text-base font-serif font-bold text-emerald-700">{accepted}</div>
          <div className="text-[10px] uppercase tracking-wider text-emerald-700/80">Joined</div>
        </div>
        <div className="bg-white rounded-lg border border-violet-200 p-2 text-center">
          <div className="text-base font-serif font-bold text-violet-700">{reserved}</div>
          <div className="text-[10px] uppercase tracking-wider text-violet-700/80">Reserved</div>
        </div>
        <div className="bg-white rounded-lg border border-slate-200 p-2 text-center">
          <div className="text-base font-serif font-bold text-slate-700">{returned}</div>
          <div className="text-[10px] uppercase tracking-wider text-slate-700/80">Returned</div>
        </div>
        <div className="bg-white rounded-lg border border-rose-200 p-2 text-center">
          <div className="text-base font-serif font-bold text-rose-700">{failed}</div>
          <div className="text-[10px] uppercase tracking-wider text-rose-700/80">Failed</div>
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-xl border border-amber-200 max-h-72 overflow-y-auto">
        {loading && invites.length === 0 ? (
          <div className="p-6 flex items-center justify-center text-amber-800/70 text-sm gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading invites…
          </div>
        ) : error ? (
          <div className="p-4 text-sm text-red-700/80 text-center">{error}</div>
        ) : invites.length === 0 ? (
          <div className="p-6 text-center text-amber-800/70 text-sm flex flex-col items-center gap-2">
            <Inbox className="w-6 h-6 text-amber-400" />
            No invites sent yet. Use the Link, Email, or Contacts tabs to invite family.
          </div>
        ) : (
          <ul className="divide-y divide-amber-100">
            {invites.map(inv => {
              const state = computeState(inv);
              const recipient = inv.recipient_email || inv.recipient_phone || 'Shared link';
              const isFailed = state === 'failed';
              const isTargeted =
                Boolean(inv.target_member_id);
              const isCopied = copiedId === inv.id;
              return (
                <li key={inv.id} className="p-3">
                  <div className="flex items-center gap-3">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        state === 'accepted'
                          ? 'bg-emerald-100 text-emerald-700'
                          : state === 'failed'
                          ? 'bg-rose-100 text-rose-700'
                          : state === 'opened'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}
                    >
                      {channelIcon(inv.channel)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="text-sm font-medium text-amber-950 truncate">
                          {recipient}
                        </div>
                        <span
                          className={`shrink-0 text-[9px] uppercase tracking-wide rounded-full px-1.5 py-0.5 border ${
                            inv.invite_type ===
                            'SPONSORED_SEAT'
                              ? 'text-violet-700 bg-violet-50 border-violet-200'
                              : 'text-amber-700 bg-amber-50 border-amber-200'
                          }`}
                        >
                          {inv.invite_type ===
                          'SPONSORED_SEAT'
                            ? 'Sponsored seat'
                            : 'Family invite'}
                        </span>
                      </div>
                      <div className="text-[11px] text-amber-800/70 flex items-center gap-1.5 flex-wrap">
                        <span>{channelLabel(inv.channel)}</span>
                        <span>•</span>
                        <span>{formatRelative(inv.created_at)}</span>
                        {inv.opened_at && state !== 'accepted' && (
                          <>
                            <span>•</span>
                            <span className="text-blue-700">Opened {formatRelative(inv.opened_at)}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <StatusPill state={state} />
                  </div>

                  {/* Failure / manual-approve fallback row */}
                  {isFailed && (
                    <div className="mt-2 ml-11 rounded-lg bg-rose-50/70 border border-rose-200 p-2.5">
                      <div className="text-[11px] text-rose-800 mb-2 leading-snug">
                        <span className="font-semibold">Auto-link failed.</span>{' '}
                        {inv.failure_reason
                          ? <span className="italic">{inv.failure_reason}</span>
                          : 'The recipient opened the link but couldn\'t be attached to your tree.'}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyLink(inv)}
                          className="h-7 text-[11px] gap-1 border-rose-300 text-rose-800 hover:bg-rose-100"
                        >
                          {isCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                          {isCopied ? 'Copied' : 'Copy link to resend'}
                        </Button>
                        {!isTargeted && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markAsHandled(inv)}
                            className="h-7 text-[11px] gap-1 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                          >
                            <ShieldCheck className="w-3 h-3" />
                            Mark as joined
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Quick "copy link" for any non-accepted row that has a token */}
                  {!isFailed && state !== 'accepted' && inv.token && (
                    <div className="mt-1.5 ml-11">
                      <button
                        type="button"
                        onClick={() => copyLink(inv)}
                        className="text-[11px] text-amber-800 hover:text-amber-950 underline-offset-2 hover:underline inline-flex items-center gap-1"
                      >
                        {isCopied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                        {isCopied ? 'Copied' : 'Copy invite link to resend'}
                      </button>
                      {isTargeted && (
                        <button
                          type="button"
                          onClick={() =>
                            void cancelTargetedInvite(inv)
                          }
                          className="ml-3 text-[11px] text-rose-700 hover:text-rose-900 underline-offset-2 hover:underline"
                        >
                          Cancel invitation
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <p className="text-[11px] text-amber-800/60 text-center">
        Showing your last {Math.min(invites.length, 50)} invite{invites.length === 1 ? '' : 's'}.
        Failed invites can be resent or manually marked as joined.
      </p>
    </div>
  );
};

export default SentInvitesPanel;
